import type { StoreCart } from "@medusajs/types";
import { useForm, useStore } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { AddressSelects } from "#/components/checkout/AddressSelects";
import { FormField } from "#/components/checkout/FormField";
import { OrderSummary } from "#/components/checkout/OrderSummary";
import { PaymentMethodPicker } from "#/components/checkout/PaymentMethodPicker";
import { ShippingMethodPicker } from "#/components/checkout/ShippingMethodPicker";
import { readAttribution } from "#/lib/attribution";
import {
  buildCartAddressUpdate,
  type CartAddressUpdate,
  composeAddress2,
} from "#/lib/checkout/cart-address";
import {
  availablePickupHubs,
  findPickupHub,
  isPickupShippingOption,
  pickupMetadata,
} from "#/lib/checkout/pickup";
import {
  isUnitedStates,
  US_SHIPPING_RESTRICTION_MESSAGE,
  useRestrictedCartItems,
  useRestrictedHGBGCHub,
} from "#/lib/checkout/shipping-restrictions";
import { isCheckoutPushThroughEnabled } from "#/lib/checkout-settings";
import { medusa } from "#/lib/medusa";
import { getCartTotals } from "#/lib/medusa-types/commerce";
import type {
  PickupHub,
  PickupReservation,
} from "#/lib/medusa-types/pickup";
import { notify } from "#/lib/notify";
import {
  computeConvenienceFee,
  type PaymongoBilling,
} from "#/lib/payment-types";
import { ROUTES } from "#/lib/routes";
import type {
  PaymentMethodDraft,
  PickupDetailsType,
} from "#/lib/schemas/checkout";
import {
  buildPaymentMethodMetadata,
  checkoutDraftSchema,
  checkoutSearchSchema,
  EMPTY_CHECKOUT_DRAFT,
  EMPTY_PICKUP_DETAILS,
} from "#/lib/schemas/checkout";
import { useDebouncedValue } from "#/lib/useDebouncedValue";
import { useSession } from "#/lib/useSession";
import { addressQueries } from "#/queries/addresses";
import { cartKeys } from "#/queries/cart";
import { checkoutKeys, checkoutQueries } from "#/queries/checkout";
import { orderKeys } from "#/queries/orders";
import {
  pickupQueries,
  releasePickupSlot,
  reservePickupSlot,
  useReleasePickupSlot,
} from "#/queries/pickup";
import { createPaymentViaDragonpay } from "#/server/functions/dragonpay";
import { generateTxnId } from "#/services/dragonpay-shared";
import {
  attachPaymentMethodToIntent,
  createPaymentMethod,
  type PaymongoPaymentMethod,
} from "#/services/paymongo-browser";
import { selectHasHydrated, useCart, useCartStore } from "#/store/cart";
import {
  checkPromoCode,
  deductCouponsFromTotal,
  type PromoCheckReason,
  removePromoCode,
  requiresCartEmail,
} from "#/server/functions/coupons";

const PAYMONGO_PROVIDER_ID = "pp_paymongo_paymongo";
const MANUAL_PROVIDER_ID = "pp_system_default";

const EMAIL_SHAPE = /^\S+@\S+\.\S+$/;

// The pre-charge gate 409s when a recent order matches this cart's email or
// phone with overlapping items (duplicate-purchase guard). The SDK surfaces
// it as a FetchError whose `status` carries the HTTP code.
function isDuplicateOrderRejection(err: unknown): err is Error {
  return err instanceof Error && Reflect.get(err, "status") === 409;
}

const paymongoSessionSchema = z.object({
  intent_id: z.string(),
  client_key: z.string().nullable(),
});

async function confirmPickupHold(
  cartId: string,
  details: PickupDetailsType,
  hubs: PickupHub[],
): Promise<PickupReservation> {
  const { hub, pickupDate, timeSlotId } = details;
  if (!findPickupHub(hubs, hub) || !pickupDate || !timeSlotId) {
    throw new Error("Choose a pickup hub, date and time before placing your order.");
  }
  return reservePickupSlot({
    cart_id: cartId,
    hub,
    pickup_date: pickupDate,
    time_slot_id: timeSlotId,
  });
}

async function dropPickupHold(
  cart: Pick<StoreCart, "id" | "completed_at">,
): Promise<null> {
  if (cart.completed_at) return null;
  try {
    await releasePickupSlot(cart.id);
  } catch (err) {
    console.error(`checkout: releasing the pickup hold on ${cart.id} failed:`, err);
  }
  return null;
}

async function createPaymongoPaymentMethod(
  pm: Extract<PaymentMethodDraft, { kind: "paymongo" }>,
  billing: PaymongoBilling,
): Promise<PaymongoPaymentMethod> {
  if (pm.channel === "card") {
    const card = pm.cardDetails;
    if (!card) throw new Error("Card details required");
    const [mm, yy] = card.expires_at.split("/");
    if (!mm || !yy) throw new Error("Invalid card expiry");
    return createPaymentMethod({
      type: "card",
      card: {
        card_number: card.account_number,
        exp_month: Number.parseInt(mm, 10),
        exp_year: 2000 + Number.parseInt(yy, 10),
        cvc: card.ccv,
      },
      billing,
    });
  }
  if (pm.channel === "dob") {
    if (!pm.bankCode) throw new Error("Bank code required for DOB");
    return createPaymentMethod({
      type: "dob",
      details: { bank_code: pm.bankCode },
      billing,
    });
  }
  return createPaymentMethod({ type: pm.channel, billing });
}

export const Route = createFileRoute("/checkout")({
  validateSearch: checkoutSearchSchema,
  loader: async ({ context: { queryClient } }) => {
    await Promise.all([
      queryClient.ensureQueryData(addressQueries.countries()),
      queryClient.ensureQueryData(addressQueries.regions("ph")),
      queryClient.ensureQueryData(pickupQueries.hubs()),
    ]);
  },
  head: () => ({ meta: [{ title: "Checkout" }] }),
  component: CheckoutPage,
});

function CheckoutPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasHydrated = useCartStore(selectHasHydrated);
  const clearCart = useCartStore((s) => s.clearCart);
  const setLastOrderId = useCartStore((s) => s.setLastOrderId);
  const { data: cart, isPending } = useCart();
  const { customer, isAuthenticated } = useSession();

  const accountEmail = isAuthenticated ? (customer?.email ?? null) : null;

  const cartId = cart?.id ?? null;
  const regionId = cart?.region_id ?? null;

  const form = useForm({
    defaultValues: EMPTY_CHECKOUT_DRAFT,
    validators: {
      onChange: checkoutDraftSchema,
      onSubmit: checkoutDraftSchema,
    },
    onSubmit: ({ value }) => placeOrder.mutateAsync(value),
  });

  const address = useStore(form.store, (state) => state.values.address);
  const debouncedAddress = useDebouncedValue(address, 150);
  // Email only syncs to the cart once blurred, or after sitting valid and
  // untouched for a while (covers browser autofill, which may never focus the
  // field): syncing per keystroke minted a guest customer row for every
  // partial address typed (one shopper produced 12), and half-typed emails
  // became order emails.
  const [committedEmail, setCommittedEmail] = useState("");
  const settledEmail = useDebouncedValue(address.email, 1500).trim();
  const emailForSync =
    settledEmail === address.email.trim() && EMAIL_SHAPE.test(settledEmail)
      ? settledEmail
      : committedEmail;
  const addressPayload = buildCartAddressUpdate({
    ...debouncedAddress,
    email: accountEmail ?? emailForSync,
  });
  const addressPayloadKey = addressPayload
    ? JSON.stringify(addressPayload)
    : null;
  const addressComplete = buildCartAddressUpdate(address) !== null;
  const addressApplied =
    addressComplete &&
    cart?.shipping_address?.country_code?.toLowerCase() ===
      address.country_code.trim().toLowerCase();

  const { titles: restrictedTitles } = useRestrictedCartItems(cart);
  const usShippingBlocked = isUnitedStates(address.country_code) && restrictedTitles.length > 0;
  const { isHgBGCHubRestricted } = useRestrictedHGBGCHub(cart);
  const releasePickup = useReleasePickupSlot();

  const persistCartAddress = useMutation({
    mutationFn: async (payload: CartAddressUpdate) => {
      if (!cartId) return;
      await medusa.store.cart.update(cartId, payload);
      await queryClient.invalidateQueries({
        queryKey: cartKeys.detail(cartId),
      });
      await queryClient.invalidateQueries({
        queryKey: checkoutKeys.shippingOptions(cartId),
      });
    },
    onError: (err) => {
      notify.error("Couldn't save shipping address", {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  useEffect(() => {
    if (!cartId || !addressPayload) return;
    persistCartAddress.mutate(addressPayload);
  }, [cartId, addressPayloadKey]);



  const persistShippingMethod = useMutation({
    mutationFn: async (optionId: string) => {
      if (!cartId) return;
      await medusa.store.cart.addShippingMethod(cartId, {
        option_id: optionId,
      });
      await queryClient.invalidateQueries({
        queryKey: cartKeys.detail(cartId),
      });
    },
  });

  const paymentMethod = useStore(
    form.store,
    (state) => state.values.payment_method,
  );

  const pickupDetails = useStore(
    form.store,
    (state) => state.values.pickupDetails,
  );

  const shippingOptionsQuery = useQuery(
    checkoutQueries.shippingOptions(cartId),
  );

  const placeOrder = useMutation({
    mutationFn: async (draft: typeof EMPTY_CHECKOUT_DRAFT) => {
      if (!cart) throw new Error("Cart not loaded");
      if (usShippingBlocked) throw new Error(US_SHIPPING_RESTRICTION_MESSAGE);
      const shippingOption = shippingOptionsQuery.data?.find(
        (o) => o.id === draft.shipping_option_id,
      );
      if (!shippingOption) throw new Error("Pick a shipping option");

      const pickupHubs = await queryClient.ensureQueryData(
        pickupQueries.hubs(),
      );
      const pickupReservation = isPickupShippingOption(shippingOption)
        ? await confirmPickupHold(
            cart.id,
            draft.pickupDetails,
            availablePickupHubs(pickupHubs, isHgBGCHubRestricted),
          )
        : await dropPickupHold(cart);

      const { cart: priced } = await medusa.store.cart.addShippingMethod(
        cart.id,
        { option_id: draft.shipping_option_id },
      );
      queryClient.setQueryData(cartKeys.detail(cart.id), priced);

      // The convenience fee shown to the customer (rate × subtotal+shipping for
      // the chosen PayMongo channel). Computed off the priced cart so it matches
      // OrderSummary, then both persisted to metadata and added to the charged
      // amount below. Dragonpay/unknown channels resolve to 0.
      const feeTotals = getCartTotals(priced);
      const convenienceFee = computeConvenienceFee(
        draft.payment_method.channel,
        feeTotals.subtotal + feeTotals.shippingTotal,
        draft.address.country_code,
      );

      // Record the chosen payment method on the cart so it carries onto the
      // order at completion (Medusa copies cart.metadata -> order.metadata),
      // making it visible in the admin. For dragonpay "any" the real bank is
      // refined later from the webhook procid. `convenience_fee` is a flat
      // primitive so the admin metadata editor renders it.
      await medusa.store.cart.update(priced.id, {
        // The blur-gated sync can miss an email edited right before an
        // Enter-key submit, so the final value is written here regardless.
        email: accountEmail ?? draft.address.email.trim(),
        metadata: {
          ...(priced.metadata ?? {}),
          ...pickupMetadata(pickupReservation, pickupHubs),
          ...buildPaymentMethodMetadata(draft.payment_method),
          // The one insertion point that reaches the order, for the same
          // reason the payment method does. Read by the CRM purchase sync off
          // order.metadata.
          ...readAttribution(),
          convenience_fee: convenienceFee,
        },
      });

      let redirectUrl: string | undefined;

      if (draft.payment_method.kind === "paymongo") {
        const pm = draft.payment_method;
        const { payment_collection } =
          await medusa.store.payment.initiatePaymentSession(priced, {
            provider_id: PAYMONGO_PROVIDER_ID,
            data: {
              channel: pm.channel,
              ...(pm.bankCode ? { bank_code: pm.bankCode } : {}),
              convenience_fee: convenienceFee,
            },
          });
        const session = payment_collection.payment_sessions?.find(
          (s) => s.provider_id === PAYMONGO_PROVIDER_ID,
        );
        const parsed = paymongoSessionSchema.safeParse(session?.data);
        if (!parsed.success) {
          throw new Error(
            "PayMongo session missing — provider returned no data",
          );
        }
        const sessionData = parsed.data;
        if (!sessionData.client_key) {
          throw new Error("PayMongo intent missing client_key");
        }

        const line2 = composeAddress2(draft.address);
        const billing = {
          name: `${draft.address.first_name} ${draft.address.last_name}`.trim(),
          email: draft.address.email,
          phone: draft.address.phone,
          address: {
            line1: draft.address.address_1,
            line2,
            city: draft.address.city,
            state: draft.address.province,
            postal_code: draft.address.postal_code,
            country: draft.address.country_code.toUpperCase().slice(0, 2),
          },
        };

        const paymentMethod = await createPaymongoPaymentMethod(pm, billing);
        const callbackUrl = new URL(
          "/api/callbacks/paymongo",
          window.location.origin,
        );
        callbackUrl.searchParams.set("cart_id", priced.id);
        const attach = await attachPaymentMethodToIntent({
          intentId: sessionData.intent_id,
          paymentMethodId: paymentMethod.data.id,
          clientKey: sessionData.client_key,
          returnUrl: callbackUrl.toString(),
        });
        redirectUrl =
          attach.data.attributes.next_action?.redirect?.url ?? undefined;
      }

      if (draft.payment_method.kind === "dragonpay") {
        await medusa.store.payment.initiatePaymentSession(priced, {
          provider_id: MANUAL_PROVIDER_ID,
          data: {},
        });
        const payment = await createPaymentViaDragonpay({
          data: {
            paymentMethod: draft.payment_method.channel,
            txnid: generateTxnId(),
            total: Number(getCartTotals(priced).total) + convenienceFee,
            ccy: "PHP",
            email: draft.address.email,
            param1: priced.id,
            billingDetails: {
              firstName: draft.address.first_name,
              middleName: "",
              lastName: draft.address.last_name,
              address1: draft.address.address_1,
              address2: draft.address.address_2 || "",
              city: draft.address.city,
              province: draft.address.province,
              country: draft.address.country_code.toUpperCase().slice(0, 2),
              postcode: draft.address.postal_code,
              phone: draft.address.phone,
              email: draft.address.email,
            },
          },
        });
        redirectUrl = payment.Url;
      }

      if (redirectUrl) {
        if (await isCheckoutPushThroughEnabled()) {
          try {
            await medusa.store.cart.complete(priced.id);
          } catch (err) {
            console.error(
              `checkout push-through: cart.complete failed for ${priced.id}, falling back to gateway callback completion:`,
              err,
            );
          }
        }
        return { kind: "redirect" as const, redirectUrl };
      }
      const result = await medusa.store.cart.complete(priced.id);
      if (result.type === "order") {
        return { kind: "order" as const, orderId: result.order.id };
      }
      return { kind: "cart" as const, message: result.error.message };
    },
    onSuccess: async (result) => {
      if (result.kind === "redirect") {
        window.location.assign(result.redirectUrl);
        return;
      }
      if (result.kind === "cart") {
        notify.info("Payment requires another step", {
          description: result.message,
        });
        return;
      }
      notify.success("Order placed");
      setLastOrderId(result.orderId);
      clearCart();
      await queryClient.invalidateQueries({ queryKey: orderKeys.all });
      await navigate({ to: "/order/$id", params: { id: result.orderId } });
    },
    onError: (err) => {
      if (isDuplicateOrderRejection(err) && cartId) {
        const proceed = window.confirm(
          `${err.message}\n\nPlace this new order anyway?`,
        );
        if (!proceed) return;
        void medusa.store.cart
          .update(cartId, {
            metadata: { ...(cart?.metadata ?? {}), duplicate_ack: true },
          })
          .then(() => form.handleSubmit())
          .catch(() => {
            notify.error("Couldn't place order", { description: err.message });
          });
        return;
      }
      notify.error("Couldn't place order", {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  if (!hasHydrated || isPending) return <SkeletonCheckout />;

  if (!cart?.items || cart.items.length === 0) {
    return (
      <section className="mx-auto max-w-[720px] px-6 py-16">
        <h1 className="font-bold text-3xl text-foreground tracking-tight">
          Checkout
        </h1>
        <p className="mt-4 text-muted-foreground text-sm">
          Your cart is empty.{" "}
          <Link to={ROUTES.shop} className="underline">
            Browse the shop
          </Link>
          .
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-[840px] px-6 py-16">
      <h1 className="m-0 font-bold font-display text-3xl text-lp-green-dk">
        Checkout
      </h1>
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10">
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <h2 className="m-0 mt-4 font-display font-semibold text-lp-green-dk text-xl">
            Shipping address
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="address.first_name">
              {(field) => (
                <FormField
                  field={field}
                  label="First name"
                  autoComplete="given-name"
                />
              )}
            </form.Field>
            <form.Field name="address.last_name">
              {(field) => (
                <FormField
                  field={field}
                  label="Last name"
                  autoComplete="family-name"
                />
              )}
            </form.Field>
          </div>
          <form.Field name="address.email">
            {(field) => (
              <FormField
                field={field}
                label="Email"
                type="email"
                autoComplete="email"
                description="Your order confirmation and receipt go here — double-check it."
                onBlur={() =>
                  setCommittedEmail(String(field.state.value ?? "").trim())
                }
              />
            )}
          </form.Field>
          <form.Field name="address.phone">
            {(field) => (
              <FormField field={field} label="Phone" autoComplete="tel" />
            )}
          </form.Field>
          <form.Field name="address.address_1">
            {(field) => (
              <FormField
                field={field}
                label="Address line 1"
                autoComplete="address-line1"
              />
            )}
          </form.Field>
          <form.Field name="address.address_2">
            {(field) => (
              <FormField
                field={field}
                label="Address line 2 (optional)"
                autoComplete="address-line2"
              />
            )}
          </form.Field>
          <form.Subscribe
            selector={(state) => ({
              country_code: state.values.address.country_code,
              province: state.values.address.province,
              city: state.values.address.city,
              barangay: state.values.address.barangay,
            })}
          >
            {(addr) => (
              <AddressSelects
                value={addr}
                onChange={(next) => {
                  form.setFieldValue("address.country_code", next.country_code);
                  form.setFieldValue("address.province", next.province);
                  form.setFieldValue("address.city", next.city);
                  form.setFieldValue("address.barangay", next.barangay);
                }}
              />
            )}
          </form.Subscribe>
          <form.Field name="address.postal_code">
            {(field) => (
              <FormField
                field={field}
                label="Postal code"
                autoComplete="postal-code"
              />
            )}
          </form.Field>

          {usShippingBlocked && (
            <p
              role="alert"
              className="m-0 rounded-2xl border border-destructive/40 bg-destructive/5 px-4 py-3 font-display text-destructive text-sm"
            >
              {US_SHIPPING_RESTRICTION_MESSAGE}
              <span className="mt-1 block text-destructive/80 text-xs">
                Restricted: {restrictedTitles.join(", ")}
              </span>
            </p>
          )}

          <h2 className="m-0 mt-4 font-display font-semibold text-lp-green-dk text-xl">
            Shipping
          </h2>
          <form.Field name="shipping_option_id">
            {(field) => (
              <div className="flex flex-col gap-2">
                <ShippingMethodPicker
                  cartId={cartId}
                  province={address.province}
                  value={field.state.value || null}
                  addressComplete={addressComplete}
                  addressApplied={addressApplied}
                  addressSaving={persistCartAddress.isPending}
                  addressError={
                    persistCartAddress.isError
                      ? persistCartAddress.error instanceof Error
                        ? persistCartAddress.error.message
                        : "Please review the address fields."
                      : null
                  }
                  isShippingRestricted={usShippingBlocked}
                  isHgBGCHubRestricted={isHgBGCHubRestricted}
                  pickupDetails={pickupDetails}
                  onPickupDetailsChange={(next) =>
                    form.setFieldValue("pickupDetails", next)
                  }
                  onChange={(id) => {
                    field.handleChange(id);
                    persistShippingMethod.mutate(id);
                    const picked = shippingOptionsQuery.data?.find(
                      (o) => o.id === id,
                    );
                    if (picked && !isPickupShippingOption(picked)) {
                      form.setFieldValue("pickupDetails", EMPTY_PICKUP_DETAILS);
                      if (cart && !cart.completed_at) {
                        releasePickup.mutate(cart.id);
                      }
                    }
                  }}
                />
                {field.state.meta.isTouched &&
                  field.state.meta.errors.length > 0 && (
                    <p
                      role="alert"
                      className="font-display text-destructive text-xs"
                    >
                      {field.state.meta.errors
                        .map((e) =>
                          typeof e === "string" ? e : (e?.message ?? "Invalid"),
                        )
                        .join(", ")}
                    </p>
                  )}
              </div>
            )}
          </form.Field>

          <h2 className="m-0 mt-4 font-display font-semibold text-lp-green-dk text-xl">
            Payment
          </h2>
          <form.Field name="payment_method">
            {(field) => (
              <PaymentMethodPicker
                regionId={regionId}
                value={field.state.value}
                onChange={(next) => field.handleChange(next)}
              />
            )}
          </form.Field>

          <form.Subscribe selector={(state) => state.values.address.email}>
            {(email) =>
              email.trim() ? (
                <p className="m-0 mt-4 font-display text-lp-text-lt text-sm">
                  Your order confirmation will be sent to{" "}
                  <strong className="text-lp-text-dk">{email.trim()}</strong>.
                  Double-check it before paying.
                </p>
              ) : null
            }
          </form.Subscribe>
          <form.Subscribe
            selector={(state) => ({
              canSubmit: state.canSubmit,
              isSubmitting: state.isSubmitting,
            })}
          >
            {({ canSubmit, isSubmitting }) => (
              <button
                type="submit"
                disabled={
                  !canSubmit ||
                  isSubmitting ||
                  placeOrder.isPending ||
                  !paymentMethod.channel ||
                  usShippingBlocked
                }
                className="mt-4 h-14 cursor-pointer rounded-full border-0 bg-lp-green-mid font-display font-semibold text-lg text-white hover:bg-lp-green-dk disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting || placeOrder.isPending
                  ? "Placing order…"
                  : "Place order"}
              </button>
            )}
          </form.Subscribe>
        </form>

        <div className="flex flex-col gap-4">
          <PromoCodeInput cartId={cartId} />
          <OrderSummary paymentMethod={paymentMethod} countryCode={address.country_code} />
        </div>
      </div>
    </section>
  );
}

interface PromoCodeInputProps {
  cartId: string | null;
}

class PromoError extends Error {
  reason: PromoCheckReason;
  constructor(message: string, reason: PromoCheckReason) {
    super(message);
    this.name = "PromoError";
    this.reason = reason;
  }
}

function PromoCodeInput({ cartId,}: PromoCodeInputProps) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [errorReason, setErrorReason] = useState<PromoCheckReason | null>(null);
  const queryClient = useQueryClient();
  const { data: cart } = useCart();
  const { customer, isAuthenticated } = useSession();

  const appliedCodes = (cart?.promotions ?? [])
    .map((p) => p.code)
    .filter((c): c is string => c != null);

  const applyPromo = useMutation({
    mutationFn: async (next: string) => {
      const result = await checkPromoCode({
        data: {
          code: next,
          email: customer?.email ?? null,
          customerId: customer?.id ?? null,
        },
      });
      if (!result.exists || !result.valid) {
        throw new PromoError(result.message, result.reason);
      }

      const { application_method } = result.promotion;
      if (!application_method?.target_type) {
        throw new PromoError(result.message, result.reason);
      }
      if (!cartId) {
        throw new PromoError("Your cart isn't ready yet.", "ok");
      }

      if (requiresCartEmail(result.promotion)) {
        if (!isAuthenticated || !customer?.email) {
          throw new PromoError(
            "Sign in to use this code — it's one use per account.",
            "email_required",
          );
        }
        if (cart?.email !== customer.email) {
          await medusa.store.cart.update(cartId, { email: customer.email });
        }
      } else if (isAuthenticated && customer?.email && !cart?.email) {
        await medusa.store.cart.update(cartId, { email: customer.email });
      }

      const deduction = await deductCouponsFromTotal({
        data: {
          cartId,
          targetType: application_method.target_type,
          promoCodes: [next],
        },
      });

      const applied = deduction.appliedCodes.some(
        (c) => c.toLowerCase() === next.toLowerCase(),
      );
      if (!applied) {
        throw new PromoError(
          "This code can't be applied to your cart — it may already have been used, or your cart has no eligible items.",
          "usage_limit",
        );
      }
    },
    onSuccess: async () => {
      setError(null);
      setErrorReason(null);
      setCode("");
      notify.success("Promo applied");
      await queryClient.invalidateQueries({ queryKey: cartKeys.all });
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : "Code not valid";
      const reason = err instanceof PromoError ? err.reason : null;
      notify.error(message);
      setError(message);
      setErrorReason(reason);
    },
  });

  const removePromo = useMutation({
    mutationFn: async (next: string) => {
      if (!cartId) throw new Error("Your cart isn't ready yet.");
      return removePromoCode({ data: { cartId, code: next } });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: cartKeys.all });
    },
    onError: (err) => {
      notify.error(err instanceof Error ? err.message : "Couldn't remove code");
    },
  });

  return (
    <div className="rounded-3xl bg-white p-5 ring-1 ring-lp-green-mid/20 flex flex-col">
      <label
        htmlFor="promo-code"
        className="block font-display font-semibold text-lp-green-dk text-sm"
      >
        Promo code
      </label>
      <div className="mt-2 flex w-full gap-2">
        <input
          id="promo-code"
          name="promo-code"
          type="text"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            if (error) {
              setError(null);
              setErrorReason(null);
            }
          }}
          autoComplete="off"
          aria-invalid={error !== null}
          aria-describedby={error ? "promo-code-error" : undefined}
          className="h-11 flex-1 w-[85%] rounded-full border-2 border-border bg-lp-green-soft/40 px-5 font-display text-base text-foreground outline-none focus-visible:border-primary focus-visible:bg-white"
        />
        <button
          type="button"
          onClick={() => applyPromo.mutate(code.trim())}
          disabled={!cartId || code.trim().length === 0 || applyPromo.isPending}
          className="cursor-pointer rounded-full border-0 bg-lp-green-mid px-5 font-display font-semibold text-sm text-white hover:bg-lp-green-dk disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {applyPromo.isPending ? "Applying…" : "Apply"}
        </button>
      </div>
      {appliedCodes.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2 p-0">
          {appliedCodes.map((applied) => (
            <li
              key={applied}
              className="flex items-center gap-2 rounded-full bg-lp-green-soft px-3 py-1 font-display text-lp-green-dk text-sm"
            >
              <span className="font-mono">{applied}</span>
              <button
                type="button"
                onClick={() => removePromo.mutate(applied)}
                disabled={removePromo.isPending}
                aria-label={`Remove promo code ${applied}`}
                className="cursor-pointer rounded-full border-0 bg-transparent leading-none text-lp-green-dk hover:text-destructive disabled:opacity-60 disabled:cursor-not-allowed"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p
          id="promo-code-error"
          role="alert"
          className="mt-2 font-display text-destructive text-xs"
        >
          {error}
        </p>
      )}
      {(errorReason === "customer_ineligible" || errorReason === "email_required") && !isAuthenticated && (
        <Link
          to={ROUTES.login}
          className="mt-2 font-display font-semibold text-lp-green-mid text-xs underline hover:text-lp-green-dk"
        >
          Sign in to use this code.
        </Link>
      )}
    </div>
  );
}

function SkeletonCheckout() {
  return (
    <section className="mx-auto max-w-[720px] px-6 py-16">
      <div className="h-10 w-48 animate-pulse rounded-full bg-lp-green-soft" />
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10">
        <div className="flex flex-col gap-3">
          <div className="h-12 animate-pulse rounded-full bg-lp-green-soft" />
          <div className="h-12 animate-pulse rounded-full bg-lp-green-soft" />
          <div className="h-12 animate-pulse rounded-full bg-lp-green-soft" />
        </div>
        <div className="h-64 animate-pulse rounded-3xl bg-lp-green-soft" />
      </div>
    </section>
  );
}
